import { KeyboardEvent, useState } from "react";
import { Text } from "@merge-api/merge-javascript-shared";
import { X } from "lucide-react";
import clsx from "clsx";

interface Props {
  label: string;
  values: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  /** Tenants are free-form names, so they opt out of UUID shape checking. */
  freeForm?: boolean;
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Entry for a rule's id dimensions. There is no name lookup, so a malformed id
// is flagged on its chip; that is the only guardrail against a bad paste.
const UuidChipsField = ({ label, values, onChange, placeholder, freeForm = false }: Props) => {
  const [draft, setDraft] = useState("");

  const commit = (raw: string) => {
    const added = raw
      .split(/[\s,]+/)
      .map((token) => token.trim())
      .filter(Boolean)
      .filter((token) => !values.includes(token));
    if (added.length > 0) onChange([...values, ...added]);
    setDraft("");
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      commit(draft);
      return;
    }
    if (event.key === "Backspace" && draft === "" && values.length > 0) {
      onChange(values.slice(0, -1));
    }
  };

  const remove = (value: string) => onChange(values.filter((existing) => existing !== value));

  return (
    <div>
      <div className="mb-1 flex items-baseline gap-2">
        <Text variant="sm" className="text-gray-60">
          {label}
        </Text>
        {values.length > 0 && (
          <Text variant="sm" className="text-gray-40">
            {values.length}
          </Text>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-1.5 rounded-md border border-gray-20 bg-white p-1.5">
        {values.map((value) => {
          const malformed = !freeForm && !UUID_PATTERN.test(value);
          return (
            <span
              key={value}
              className={clsx(
                "flex items-center gap-1 rounded px-1.5 py-0.5 font-mono text-sm",
                malformed ? "bg-red-0 text-red-60" : "bg-gray-0 text-gray-80"
              )}
              title={malformed ? "Not a valid UUID" : undefined}
            >
              {value}
              <button
                onClick={() => remove(value)}
                aria-label={`Remove ${value}`}
                className="text-gray-40 hover:text-gray-80"
              >
                <X size={12} />
              </button>
            </span>
          );
        })}
        <input
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={onKeyDown}
          onBlur={() => commit(draft)}
          placeholder={values.length === 0 ? placeholder : ""}
          className="min-w-[220px] flex-1 border-0 bg-transparent px-1 py-0.5 font-mono text-sm text-gray-90 outline-none"
        />
      </div>
    </div>
  );
};

export default UuidChipsField;
